// src/portal-app/operations/ExpensesScreen.js
//
// Plan 4 Task 6 — Expenses screen, migrated to the P4 pattern (spec §5). See
// operations/TransactionsScreen.js:32-55 for the pattern (and its file
// header) this follows; more/InventoryScreen.js (Plan 4 Task 5) is the
// closest reviewed model. This header only calls out decisions specific to
// Expenses.
//
// Daily vs Overhead — VERIFIED as a genuine sibling-view switch, not a
// client-side filter dressed up as one (the exact defect a Plan 4 Task 4
// review caught on Products' Active/Inactive toggle — see progress.md).
// Daily hits expenseService.getExpensesAfterStartOfDay(salesDate) (today's
// business day only); Overhead hits expenseService.getOverheads(30) (a
// rolling 30-day window, no business-day dependency at all). Two different
// service calls, two different implicit date windows, two independent
// SurfaceStates statuses — this is exactly the case the P4 recipe permits a
// SegmentedControl for. It stays outside FilterSheet.
//
// Hero — FIX ROUND 1 (review Finding 1): tab-scoped, not combined. Daily is
// today only; Overhead is a rolling 30 days — two non-overlapping windows of
// different sizes, so their sum has no natural meaning ("Total Spend:
// £2200.00" under two rows totalling £200 on the Daily tab explains
// nothing). The recipe's own rule is "the hero summarises the list below
// it" — Transactions' cash/card ShareBar works because the split lives
// WITHIN the one range whose rows are rendered below (same denominator);
// here the split would be BETWEEN two tabs with different denominators, so
// there is no valid split to draw and no ShareBar. Each tab now renders its
// own hero showing only its own total — gated the same way Transactions
// gates its hero to the transactions tab.
//
// Negative values (Task 3A note) — `Expense.total_cost`
// (backend/app/models/models.py:614) has no non-negative constraint at the
// DB or Pydantic layer. The receipt-scan endpoint clamps `total_cost =
// max(0, ...)` (backend/app/routers/expenses.py:282); the manual create
// form's price/qty inputs use client-side `min="0"` only — NOT verified as
// server-enforced on the main create endpoint (backend is out of scope for
// this task). No refund/credit flow was found anywhere in the expenses
// router/model. With the hero now tab-scoped and ShareBar-free, this note
// is moot for this screen either way — recorded for completeness only.
//
// Row — CondensedRow: name = product (description), value = amount, pill
// unused (no per-row status/severity exists for an expense — using it to
// hold something like the authorizer's name would be stylistically
// inconsistent with every other pill in the kit, which are all status/
// category badges), secondary composed as "Category · Date/Time · By
// {authorizer}[ · Notes]". FIX ROUND 1 (review Finding 3): authorizer is
// composed in, not dropped — it answers "who logged/approved this spend",
// the same kind of accountability field `supplier` was on Inventory rows
// (Task 5), and unlike quantity×price (a pure redundant restatement of the
// amount already shown, correctly still dropped) it carries information
// nothing else on the row does. There is no expense detail/edit screen on
// this surface (the pre-migration header's "No edit — skipped" note is
// still true), so composing it into secondary is the only way to keep it
// visible at all. Omitted cleanly (no dangling separator, no "By —") when
// unset. CondensedRow sets `title={secondary}` on that span, so a visually
// truncated tail is still reachable via hover/long-press.
//
// Also fixed while in the file (minor, reviewer's call accepted): the Daily
// tab's secondary now shows time-of-day (`fmtTime`), not date — every row
// on a same-day list shares the same date, so date carried no information
// there. The Overhead tab (rolling 30 days) still shows date, where it
// does. Dropped from the row: only quantity×price remains dropped, for the
// reason above.
//
// No FilterButton/FilterSheet on this screen — there is no categorical
// field to filter by beyond the daily/overhead split itself, which is
// already the SegmentedControl above. The recipe's control row is a
// maximum, not a quota; adding one here to match the template would be
// exactly the anti-pattern the brief calls out.
//
// PullToRefresh (Task 2) wraps each tab's count line + list, calling that
// tab's own fetch (fetchDaily / fetchOverheads). Disabled while any sheet/
// overlay is open (form, scan, delete confirm), matching Inventory.
//
// Stale-response guards (dailySeq / overheadSeq): unchanged from the
// pre-migration screen.
//
// CreateExpenseForm, the receipt-scan overlay/results/error sheets, and the
// FABs are UNCHANGED by this task — none is a list surface, and every
// field, payload shape and testid they use is preserved exactly.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { expenseService, sessionService, inventoryService } from '../../services/api';
import tenantStorage from '../../services/tenantStorage';
import { SurfaceStates, fmtMoney } from '../insights/kit';
import {
    SegmentedControl,
    ConfirmSheet,
    CondensedRow,
    PullToRefresh,
    ensureKitCss,
    IconCash,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.exp-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 120px;}
.exp-seg{padding:0 16px;margin-bottom:14px;}
.exp-list{display:flex;flex-direction:column;gap:10px;padding:0 16px;}

/* ---- hero — tab-scoped total (its own tab's own total only) ---- */
.exp-hero{margin:0 16px 16px;padding:18px 20px 16px;}
.exp-hero-label{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin-bottom:7px;}
.exp-hero-value{font-size:38px;font-weight:800;letter-spacing:-1.2px;
  color:var(--c-ink);line-height:1.05;margin-bottom:12px;}
.exp-hero-value:last-child{margin-bottom:0;}

/* ---- count line ---- */
.exp-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}

/* ---- no-business-day banner ---- */
.exp-banner{margin:0 16px 12px;padding:14px 16px;border-radius:18px;
  background:color-mix(in srgb,var(--c-amber) 10%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-amber) 25%,transparent);}
.exp-banner-text{font-size:13px;font-weight:600;color:var(--c-amber);
  line-height:1.55;}

/* ---- row wrap — CondensedRow plus the delete action ---- */
.exp-row-wrap{display:flex;flex-direction:column;gap:6px;}
.exp-row-actions{display:flex;justify-content:flex-end;margin-inline-start:44px;}
.exp-delete-btn{min-height:44px;min-width:44px;padding:0 10px;
  border:1.5px solid color-mix(in srgb,var(--c-red) 30%,transparent);
  border-radius:12px;
  background:color-mix(in srgb,var(--c-red) 6%,transparent);
  font-family:inherit;font-size:11px;font-weight:700;color:var(--c-red);
  cursor:pointer;display:inline-flex;align-items:center;justify-content:center;
  transition:background .15s ease,transform .12s ease;flex:none;}
.exp-delete-btn:active{transform:scale(.96);}

/* ---- FABs ---- */
.exp-fabs{position:fixed;bottom:calc(80px + var(--safe-bottom, 0px));
  inset-inline-end:20px;display:flex;flex-direction:column;gap:10px;
  z-index:200;}
.exp-fab{display:inline-flex;align-items:center;justify-content:center;gap:6px;
  min-height:52px;padding:0 18px;border-radius:26px;border:none;
  font-family:inherit;font-size:14px;font-weight:700;color:var(--c-white);
  cursor:pointer;
  box-shadow:0 4px 20px color-mix(in srgb,var(--c-ink) 18%,transparent);
  transition:transform .15s ease,box-shadow .15s ease;}
.exp-fab:active{transform:scale(.96);}
.exp-fab-add{background:var(--c-green);}
.exp-fab-scan{background:color-mix(in srgb,var(--c-blue) 90%,transparent);}

/* ---- Bottom sheet form ---- */
.exp-sheet-backdrop{position:fixed;inset:0;z-index:500;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.exp-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:20px 20px calc(20px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  max-height:90vh;overflow-y:auto;}
.exp-sheet-title{font-size:18px;font-weight:800;color:var(--c-ink);
  margin-bottom:14px;letter-spacing:-.3px;}
.exp-form-field{margin-bottom:12px;}
.exp-form-label{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.7px;color:var(--c-textLight);margin-bottom:5px;
  display:block;}
.exp-form-input,.exp-form-select{display:block;width:100%;box-sizing:border-box;
  min-height:44px;padding:10px 14px;border-radius:14px;
  border:1.5px solid color-mix(in srgb,var(--c-ink) 12%,transparent);
  background:color-mix(in srgb,var(--c-surface) 60%,transparent);
  font-family:inherit;font-size:14px;font-weight:500;color:var(--c-ink);
  outline:none;appearance:none;-webkit-appearance:none;}
.exp-form-input:focus,.exp-form-select:focus{border-color:var(--c-green);}
.exp-form-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.exp-form-actions{display:flex;gap:10px;margin-top:16px;}
.exp-form-cancel{flex:1;min-height:44px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:transparent;font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;}
.exp-form-save{flex:1;min-height:44px;border:none;border-radius:16px;
  background:var(--c-green);font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;}
.exp-form-save:disabled{opacity:.5;cursor:not-allowed;}
.exp-form-error{font-size:12px;font-weight:600;color:var(--c-red);
  margin-top:8px;}
.exp-manual-btn{min-height:36px;padding:0 12px;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:10px;
  background:transparent;font-family:inherit;font-size:12px;font-weight:600;
  color:var(--c-textLight);cursor:pointer;margin-top:6px;}

/* ---- Scan overlay ---- */
.exp-scan-overlay{position:fixed;inset:0;z-index:600;
  background:color-mix(in srgb,var(--c-ink) 55%,transparent);
  display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:12px;}
.exp-scan-loading-text{font-size:16px;font-weight:700;color:var(--c-white);
  letter-spacing:-.2px;}
.exp-scan-spinner{width:36px;height:36px;border-radius:50%;
  border:3px solid color-mix(in srgb,var(--c-white) 25%,transparent);
  border-top-color:var(--c-white);
  animation:expSpin .8s linear infinite;}
@keyframes expSpin{to{transform:rotate(360deg);}}

/* ---- Scan results ---- */
.exp-scan-results{position:fixed;inset:0;z-index:600;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.exp-scan-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:20px 20px calc(20px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;
  max-height:80vh;overflow-y:auto;}
.exp-scan-title{font-size:16px;font-weight:800;color:var(--c-ink);
  margin-bottom:12px;}
.exp-scan-item{padding:12px 14px;margin-bottom:8px;cursor:pointer;
  border-radius:14px;}
.exp-scan-item-name{font-size:14px;font-weight:700;color:var(--c-ink);}
.exp-scan-item-meta{font-size:12px;font-weight:500;color:var(--c-textLight);
  margin-top:2px;}
.exp-scan-add-all{min-height:44px;width:100%;border:none;border-radius:16px;
  background:var(--c-green);font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;margin-top:8px;}
.exp-scan-close{min-height:44px;width:100%;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:transparent;font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;margin-top:8px;}

/* ---- Scan error ---- */
.exp-scan-error{position:fixed;inset:0;z-index:600;
  background:color-mix(in srgb,var(--c-ink) 45%,transparent);
  display:flex;align-items:flex-end;}
.exp-scan-error-sheet{width:100%;max-width:560px;margin:0 auto;
  padding:20px 20px calc(20px + var(--safe-bottom,0px));
  box-sizing:border-box;border-radius:24px 24px 0 0;}
.exp-scan-error-text{font-size:15px;font-weight:700;color:var(--c-red);
  margin-bottom:12px;}
.exp-scan-retry{min-height:44px;width:100%;border:none;border-radius:16px;
  background:var(--c-green);font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-white);cursor:pointer;margin-bottom:8px;}
.exp-scan-dismiss{min-height:44px;width:100%;border:1.5px solid
  color-mix(in srgb,var(--c-ink) 14%,transparent);border-radius:16px;
  background:transparent;font-family:inherit;font-size:15px;font-weight:700;
  color:var(--c-ink);cursor:pointer;}

/* ---- animations ---- */
@keyframes expSlideUp{
  from{transform:translateY(100%);opacity:0;}
  to{transform:translateY(0);opacity:1;}
}
.exp-sheet-enter,.exp-scan-sheet,.exp-scan-error-sheet{
  animation:expSlideUp 260ms ease-out both;}

@media(prefers-reduced-motion:reduce){
  .exp-sheet-enter,.exp-scan-sheet,.exp-scan-error-sheet{animation:none;}
  .exp-delete-btn:active,.exp-fab:active,.exp-scan-item:hover{
    transform:none;transition:none;}
  .exp-scan-spinner{animation-duration:2s;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'exp-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Inline SVG icons (no emoji, accessible).                           */
/* ------------------------------------------------------------------ */
const PlusIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const CameraIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
        <circle cx="12" cy="13" r="4" />
    </svg>
);

const TrashIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */
const INVENTORY_CATS = ['Inventory', 'Inventory Purchase', 'المخزون'];

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Format HH:MM:SS → 12h AM/PM local time. */
function fmtTime(timeStr) {
    if (!timeStr) return '';
    try {
        const [h, m] = timeStr.split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    } catch (_) {
        return timeStr;
    }
}

/** Get current local date as YYYY-MM-DD (never toISOString). */
function localDateStr() {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Get current local time as HH:MM:SS (never toISOString). */
function localTimeStr() {
    const d = new Date();
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
}

/* ------------------------------------------------------------------ */
/* Expense row — CondensedRow plus the delete action beneath it. See   */
/* file header for the field mapping: category, date/time, authorizer   */
/* and notes are all composed into the single secondary string; only    */
/* qty×price is dropped (pure redundant restatement of the amount).     */
/* `temporal` is the caller-supplied date-or-time string (time for the   */
/* same-day Daily tab, date for the rolling-30-day Overhead tab).        */
/* ------------------------------------------------------------------ */
function ExpenseRow({ expense, tone, temporal, currency, onDelete, t }) {
    const secondary = [
        expense.category,
        temporal || null,
        expense.authorizer ? `${t('authorizer', {}, 'By')} ${expense.authorizer}` : null,
        expense.miscellaneous || null,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <div className="exp-row-wrap" data-testid={`expense-row-${expense.expense_id}`}>
            <CondensedRow
                icon={<IconCash />}
                tone={tone}
                name={expense.product}
                value={fmtMoney(Number(expense.total_cost), currency)}
                secondary={secondary || undefined}
                testId={expense.expense_id}
            />
            <div className="exp-row-actions">
                <button
                    type="button"
                    className="exp-delete-btn"
                    data-testid="expense-delete-btn"
                    onClick={() => onDelete(expense)}
                    aria-label={`${t('delete_expense', {}, 'Delete expense')}: ${expense.product}`}
                >
                    <TrashIcon />
                </button>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* CreateExpenseForm — glass bottom sheet. UNCHANGED by this task.     */
/* ------------------------------------------------------------------ */
function CreateExpenseForm({
    open,
    onClose,
    onSaved,
    isBusinessDay,
    salesDate,
    eodMode,
    categories,
    activeSessions,
    username,
    inventoryItems,
    initialData, // optional pre-fill from receipt scan
    currency,
    t,
}) {
    const [category, setCategory] = useState('');
    const [product, setProduct] = useState('');
    const [pricePerUnit, setPricePerUnit] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [totalOverride, setTotalOverride] = useState('');
    const [notes, setNotes] = useState('');
    const [sessionId, setSessionId] = useState('');
    const [inventoryId, setInventoryId] = useState(null);
    const [manualProduct, setManualProduct] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Reset form whenever opened / initialData changes
    useEffect(() => {
        if (!open) return;
        setError('');
        setSaving(false);
        setManualProduct(false);
        setInventoryId(null);

        if (initialData) {
            setCategory(initialData.category || '');
            setProduct(initialData.product_name || '');
            setPricePerUnit(String(initialData.unit_price || ''));
            setQuantity(String(initialData.quantity || '1'));
            setTotalOverride(String(initialData.total_cost || ''));
            setNotes(initialData.notes || '');
        } else {
            setCategory(categories[0] || '');
            setProduct('');
            setPricePerUnit('');
            setQuantity('1');
            setTotalOverride('');
            setNotes('');
            setSessionId('');
        }
    }, [open, initialData, categories]);

    if (!open) return null;

    const isOverhead = !isBusinessDay;
    const isInventoryCat = INVENTORY_CATS.includes(category);
    const totalAuto = pricePerUnit && quantity
        ? (parseFloat(pricePerUnit) * parseFloat(quantity)).toFixed(2)
        : '';
    const totalDisplay = totalOverride !== '' ? totalOverride : totalAuto;

    const handleInventorySelect = (e) => {
        const id = e.target.value;
        if (!id) {
            setInventoryId(null);
            setProduct('');
            return;
        }
        const item = inventoryItems.find((i) => String(i.inventory_id) === String(id));
        if (item) {
            setInventoryId(item.inventory_id);
            setProduct(item.name);
        }
    };

    const handleSave = async () => {
        if (!category) { setError(t('expense_category', {}, 'Category is required')); return; }
        if (!product) { setError(t('expense_product', {}, 'Product is required')); return; }
        if (!pricePerUnit) { setError(t('price_per_unit', {}, 'Price per unit is required')); return; }

        setSaving(true);
        setError('');

        const isOverheadNow = !isBusinessDay;
        const expenseType = isOverheadNow ? 'overhead' : 'daily';
        const expSalesDate = isOverheadNow ? null : (salesDate || null);

        // Session linking
        let linkedSessionId = null;
        if (!isOverheadNow) {
            if (eodMode === 'per_till') {
                linkedSessionId = sessionId || null;
            } else if (activeSessions && activeSessions.length > 0) {
                linkedSessionId = activeSessions[0].session_id;
            }
        }

        const payload = {
            date: localDateStr(),
            time: localTimeStr(),
            authorizer: username || '',
            category,
            product,
            price_per_unit: parseFloat(pricePerUnit),
            quantity: parseInt(quantity, 10) || 1,
            total_cost: totalDisplay
                ? parseFloat(totalDisplay)
                : (parseFloat(pricePerUnit) * (parseInt(quantity, 10) || 1)),
            miscellaneous: notes || null,
            sales_date: expSalesDate,
            inventory_id: inventoryId || null,
            is_till_expense: false,
            expense_type: expenseType,
            ...(linkedSessionId ? { session_id: linkedSessionId } : {}),
        };

        try {
            await expenseService.createExpense(payload);
            onSaved(isOverheadNow ? 'overhead' : 'daily');
        } catch (err) {
            setError(t('failed_to_save_expense', {}, 'Failed to save expense. Please try again.'));
            setSaving(false);
        }
    };

    return (
        <div
            className="exp-sheet-backdrop"
            data-testid="expense-form-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="presentation"
        >
            <GlassCard veil className="exp-sheet exp-sheet-enter" data-testid="expense-form">
                <div className="exp-sheet-title">
                    {t(isBusinessDay ? 'add_expense' : 'add_overhead', {}, isBusinessDay ? 'Add Expense' : 'Add Overhead')}
                </div>

                {/* Category */}
                <div className="exp-form-field">
                    <label className="exp-form-label" htmlFor="exp-category">
                        {t('expense_category', {}, 'Category')}
                    </label>
                    <select
                        id="exp-category"
                        className="exp-form-select"
                        data-testid="form-category"
                        value={category}
                        onChange={(e) => {
                            setCategory(e.target.value);
                            setProduct('');
                            setInventoryId(null);
                            setManualProduct(false);
                        }}
                    >
                        {categories.map((cat) => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                </div>

                {/* Product — inventory dropdown or free-text */}
                <div className="exp-form-field">
                    <label className="exp-form-label" htmlFor="exp-product">
                        {t('expense_product', {}, 'Product')}
                    </label>
                    {isInventoryCat && !manualProduct ? (
                        <>
                            <select
                                id="exp-inventory-select"
                                className="exp-form-select"
                                data-testid="form-inventory-select"
                                value={inventoryId || ''}
                                onChange={handleInventorySelect}
                            >
                                <option value="">{t('select_item', {}, 'Select item...')}</option>
                                {inventoryItems.map((item) => (
                                    <option key={item.inventory_id} value={item.inventory_id}>
                                        {item.name}
                                        {item.unit_of_measurement ? ` (${item.unit_of_measurement})` : ''}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                className="exp-manual-btn"
                                data-testid="form-manual-btn"
                                onClick={() => { setManualProduct(true); setInventoryId(null); }}
                            >
                                {t('enter_manually', {}, 'Enter manually')}
                            </button>
                        </>
                    ) : (
                        <input
                            id="exp-product"
                            type="text"
                            className="exp-form-input"
                            data-testid="form-product"
                            value={product}
                            placeholder={t('expense_product', {}, 'Product name')}
                            onChange={(e) => setProduct(e.target.value)}
                        />
                    )}
                </div>

                {/* Price × Quantity */}
                <div className="exp-form-row">
                    <div className="exp-form-field">
                        <label className="exp-form-label" htmlFor="exp-price">
                            {t('price_per_unit', {}, 'Price / unit')}
                        </label>
                        <input
                            id="exp-price"
                            type="number"
                            min="0"
                            step="0.01"
                            className="exp-form-input exp-num"
                            data-testid="form-price"
                            value={pricePerUnit}
                            onChange={(e) => {
                                setPricePerUnit(e.target.value);
                                setTotalOverride('');
                            }}
                        />
                    </div>
                    <div className="exp-form-field">
                        <label className="exp-form-label" htmlFor="exp-qty">
                            {t('quantity', {}, 'Qty')}
                        </label>
                        <input
                            id="exp-qty"
                            type="number"
                            min="1"
                            step="1"
                            className="exp-form-input exp-num"
                            data-testid="form-quantity"
                            value={quantity}
                            onChange={(e) => {
                                setQuantity(e.target.value);
                                setTotalOverride('');
                            }}
                        />
                    </div>
                </div>

                {/* Total — auto-calculated, editable override */}
                <div className="exp-form-field">
                    <label className="exp-form-label" htmlFor="exp-total">
                        {t('total_cost', {}, 'Total')}
                    </label>
                    <input
                        id="exp-total"
                        type="number"
                        min="0"
                        step="0.01"
                        className="exp-form-input exp-num"
                        data-testid="form-total"
                        value={totalDisplay}
                        onChange={(e) => setTotalOverride(e.target.value)}
                    />
                </div>

                {/* Session picker (per_till mode only, daily expenses only) */}
                {!isOverhead && eodMode === 'per_till' && isBusinessDay && (
                    <div className="exp-form-field">
                        <label className="exp-form-label" htmlFor="exp-session">
                            {t('select_session', {}, 'Session')}
                        </label>
                        <select
                            id="exp-session"
                            className="exp-form-select"
                            data-testid="form-session-picker"
                            value={sessionId}
                            onChange={(e) => setSessionId(e.target.value)}
                        >
                            <option value="">{t('select_session', {}, 'Select session...')}</option>
                            {activeSessions.map((s) => (
                                <option key={s.session_id} value={s.session_id}>
                                    {s.employee_name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Authorizer — auto-filled */}
                <div className="exp-form-field">
                    <label className="exp-form-label" htmlFor="exp-authorizer">
                        {t('authorizer', {}, 'Authorizer')}
                    </label>
                    <input
                        id="exp-authorizer"
                        type="text"
                        className="exp-form-input"
                        data-testid="form-authorizer"
                        value={username || ''}
                        readOnly
                        onChange={() => {}}
                    />
                </div>

                {/* Notes */}
                <div className="exp-form-field">
                    <label className="exp-form-label" htmlFor="exp-notes">
                        {t('miscellaneous', {}, 'Notes (optional)')}
                    </label>
                    <input
                        id="exp-notes"
                        type="text"
                        className="exp-form-input"
                        data-testid="form-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </div>

                {error && <div className="exp-form-error">{error}</div>}

                <div className="exp-form-actions">
                    <button
                        type="button"
                        className="exp-form-cancel"
                        onClick={onClose}
                    >
                        {t('cancel', {}, 'Cancel')}
                    </button>
                    <button
                        type="button"
                        className="exp-form-save"
                        data-testid="form-save-btn"
                        disabled={saving}
                        onClick={handleSave}
                    >
                        {saving
                            ? t('saving', {}, 'Saving...')
                            : t('save', {}, 'Save')}
                    </button>
                </div>
            </GlassCard>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* ExpensesScreen                                                       */
/* ------------------------------------------------------------------ */
export default function ExpensesScreen() {
    ensureCss();
    ensureKitCss();
    const { getCurrency, username, getTenantSetting } = useAuth();
    const { t } = useLanguage();
    const currency = getCurrency();

    const dailyCats = getTenantSetting('daily_expense_categories', ['Inventory', 'Equipment', 'Maintenance', 'Other']);
    const overheadCats = getTenantSetting('overhead_expense_categories', ['Rent', 'Utilities', 'Bills', 'Other']);
    const eodMode = getTenantSetting('eod_mode', 'per_store');

    // ── Tab state ─────────────────────────────────────────────────────
    const [tab, setTab] = useState('daily');

    // ── Business day state ─────────────────────────────────────────────
    const [isBusinessDay, setIsBusinessDay] = useState(false);
    const [salesDate, setSalesDate] = useState(null);
    const [bdLoaded, setBdLoaded] = useState(false);

    // ── Daily expenses state ───────────────────────────────────────────
    const [dailyStatus, setDailyStatus] = useState('loading');
    const [dailyExpenses, setDailyExpenses] = useState([]);
    const dailySeq = useRef(0);

    // ── Overhead expenses state ────────────────────────────────────────
    const [overheadStatus, setOverheadStatus] = useState('loading');
    const [overheadExpenses, setOverheadExpenses] = useState([]);
    const overheadSeq = useRef(0);

    // ── Active sessions (for per_till picker) ─────────────────────────
    const [activeSessions, setActiveSessions] = useState([]);

    // ── Inventory items (for inventory-linked categories) ─────────────
    const [inventoryItems, setInventoryItems] = useState([]);

    // ── Create form state ──────────────────────────────────────────────
    const [formOpen, setFormOpen] = useState(false);
    const [formInitialData, setFormInitialData] = useState(null);

    // ── Delete state ───────────────────────────────────────────────────
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteReason, setDeleteReason] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);

    // ── Receipt scan state ─────────────────────────────────────────────
    const [scanLoading, setScanLoading] = useState(false);
    const [scanResults, setScanResults] = useState(null);  // null | items[]
    const [scanError, setScanError] = useState(false);
    const fileInputRef = useRef(null);

    // ── Load business day on mount ─────────────────────────────────────
    useEffect(() => {
        tenantStorage.getBusinessDayState().then((state) => {
            const bday = !!(state && state.isBusinessDay);
            const sd = (state && state.salesDate) || null;
            setIsBusinessDay(bday);
            setSalesDate(sd);
            setBdLoaded(true);
        });
    }, []);

    // ── Fetch active sessions ──────────────────────────────────────────
    useEffect(() => {
        if (eodMode === 'per_till') {
            sessionService.getActiveSessions().then((sessions) => {
                setActiveSessions(sessions || []);
            }).catch(() => {});
        }
    }, [eodMode]);

    // ── Fetch inventory items ──────────────────────────────────────────
    useEffect(() => {
        inventoryService.getAllInventory().then((items) => {
            setInventoryItems(items || []);
        }).catch(() => {});
    }, []);

    // ── Fetch daily expenses ───────────────────────────────────────────
    const fetchDaily = useCallback(async () => {
        const seq = ++dailySeq.current;
        setDailyStatus('loading');
        try {
            if (!isBusinessDay || !salesDate) {
                if (seq !== dailySeq.current) return;
                setDailyExpenses([]);
                setDailyStatus('empty');
                return;
            }
            const items = await expenseService.getExpensesAfterStartOfDay(salesDate);
            if (seq !== dailySeq.current) return;
            const filtered = (items || []).filter(
                (e) => e.expense_type === 'daily' || !e.expense_type,
            );
            setDailyExpenses(filtered);
            setDailyStatus(filtered.length === 0 ? 'empty' : 'ready');
        } catch (_) {
            if (seq !== dailySeq.current) return;
            setDailyStatus('error');
        }
    }, [isBusinessDay, salesDate]);

    // ── Fetch overhead expenses ────────────────────────────────────────
    const fetchOverheads = useCallback(async () => {
        const seq = ++overheadSeq.current;
        setOverheadStatus('loading');
        try {
            const items = await expenseService.getOverheads(30);
            if (seq !== overheadSeq.current) return;
            setOverheadExpenses(items || []);
            setOverheadStatus((items || []).length === 0 ? 'empty' : 'ready');
        } catch (_) {
            if (seq !== overheadSeq.current) return;
            setOverheadStatus('error');
        }
    }, []);

    // Fetch daily once bdLoaded
    useEffect(() => {
        if (bdLoaded) fetchDaily();
    }, [bdLoaded, fetchDaily]);

    // FIX ROUND 1 (review Finding 2): lazy on tab switch, matching
    // pre-migration behaviour exactly. The eager-on-mount fetch this task
    // originally introduced existed solely to keep the (now-removed)
    // combined hero accurate; with the hero tab-scoped (Finding 1), that
    // reason is gone, and an unconditional extra fetch on every mount is a
    // real cost on a mobile app running over cellular against a Cloud Run
    // backend with cold-start latency.
    useEffect(() => {
        if (tab === 'overhead') fetchOverheads();
    }, [tab, fetchOverheads]);

    // ── Delete handlers ────────────────────────────────────────────────
    const openDelete = useCallback((expense) => {
        setDeleteTarget(expense);
        setDeleteReason('');
        setDeleteLoading(false);
    }, []);

    const handleDelete = useCallback(async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await expenseService.deleteExpense(deleteTarget.expense_id, deleteReason);
            setDeleteTarget(null);
            setDeleteReason('');
            // Reload the correct list
            if (deleteTarget.expense_type === 'overhead') {
                await fetchOverheads();
            } else {
                await fetchDaily();
            }
        } catch (_) {
            setDeleteLoading(false);
        }
    }, [deleteTarget, deleteReason, fetchDaily, fetchOverheads]);

    // ── Create handlers ────────────────────────────────────────────────
    const handleFormSaved = useCallback((type) => {
        setFormOpen(false);
        setFormInitialData(null);
        if (type === 'overhead') {
            fetchOverheads();
        } else {
            fetchDaily();
        }
    }, [fetchDaily, fetchOverheads]);

    // ── Receipt scan handlers ──────────────────────────────────────────
    const handleFileChange = useCallback(async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;
        // Reset the input so the same file can be selected again
        if (fileInputRef.current) fileInputRef.current.value = '';
        setScanLoading(true);
        setScanResults(null);
        setScanError(false);
        try {
            const fd = new FormData();
            fd.append('receipt_image', file);
            const response = await expenseService.scanReceipt(fd);
            setScanLoading(false);
            if (response && response.items && response.items.length > 0) {
                setScanResults(response.items);
            } else {
                setScanError(true);
            }
        } catch (_) {
            setScanLoading(false);
            setScanError(true);
        }
    }, []);

    const handleScanItemTap = useCallback((item) => {
        setScanResults(null);
        setFormInitialData(item);
        setFormOpen(true);
    }, []);

    const handleBatchCreate = useCallback(async () => {
        if (!scanResults) return;
        const isOverhead = !isBusinessDay;
        const expenseType = isOverhead ? 'overhead' : 'daily';
        const expSalesDate = isOverhead ? null : salesDate;
        for (const item of scanResults) {
            try {
                await expenseService.createExpense({
                    date: localDateStr(),
                    time: localTimeStr(),
                    authorizer: username || '',
                    category: item.category || '',
                    product: item.product_name || '',
                    price_per_unit: Number(item.unit_price) || 0,
                    quantity: Number(item.quantity) || 1,
                    total_cost: Number(item.total_cost) || 0,
                    miscellaneous: item.notes || null,
                    sales_date: expSalesDate,
                    inventory_id: null,
                    is_till_expense: false,
                    expense_type: expenseType,
                });
            } catch (_) {
                // Continue with remaining items on partial failure
            }
        }
        setScanResults(null);
        if (isOverhead) {
            fetchOverheads();
        } else {
            fetchDaily();
        }
    }, [scanResults, isBusinessDay, salesDate, username, fetchDaily, fetchOverheads]);

    // ── Hero: tab-scoped total (Fix round 1, Finding 1) ─────────────────
    const dailyTotal = dailyExpenses.reduce((s, e) => s + Number(e.total_cost), 0);
    const overheadTotal = overheadExpenses.reduce((s, e) => s + Number(e.total_cost), 0);

    // ── Segmented control options ──────────────────────────────────────
    const segOptions = [
        { id: 'daily', label: t('daily_expenses', {}, 'Daily') },
        { id: 'overhead', label: t('overhead_expenses', {}, 'Overhead') },
    ];

    // ── Current tab categories ─────────────────────────────────────────
    // When no business day is open, daily expenses auto-classify as overhead,
    // so show overhead categories in the form regardless of the active tab.
    const currentCats = (tab === 'overhead' || !isBusinessDay) ? overheadCats : dailyCats;

    const ptrDisabled = formOpen || !!deleteTarget || scanLoading || !!scanResults || !!scanError;

    // Show loading until business day state is resolved
    if (!bdLoaded) {
        return (
            <div className="exp-wrap">
                <SurfaceStates status="loading" />
            </div>
        );
    }

    return (
        <div className="exp-wrap">
            {/* Tab selector — daily vs overhead hit different service calls
                with different implicit date windows (see file header), so
                this stays a SegmentedControl rather than collapsing into
                FilterSheet. OUTSIDE SurfaceStates so it never vanishes. */}
            <div className="exp-seg">
                <SegmentedControl
                    options={segOptions}
                    value={tab}
                    onChange={setTab}
                />
            </div>

            {/* Hero — tab-scoped, OUTSIDE SurfaceStates, gated to the active
                tab exactly like Transactions gates its hero to the
                transactions tab. Daily and overhead never mix (see file
                header, Fix round 1 / Finding 1). */}
            {tab === 'daily' && (
                <GlassCard veil className="exp-hero" data-testid="expenses-hero">
                    <div className="exp-hero-label">{t('total_spend', {}, 'Total Spend')}</div>
                    <div className="exp-hero-value opk-num" data-testid="hero-total">
                        {fmtMoney(dailyTotal, currency)}
                    </div>
                </GlassCard>
            )}
            {tab === 'overhead' && (
                <GlassCard veil className="exp-hero" data-testid="expenses-hero">
                    <div className="exp-hero-label">{t('total_spend', {}, 'Total Spend')}</div>
                    <div className="exp-hero-value opk-num" data-testid="hero-total">
                        {fmtMoney(overheadTotal, currency)}
                    </div>
                </GlassCard>
            )}

            {/* No-business-day banner — daily tab only, unchanged placement */}
            {tab === 'daily' && !isBusinessDay && (
                <div className="exp-banner" data-testid="no-business-day-banner">
                    <div className="exp-banner-text">
                        {t('no_business_day_banner', {}, 'No active business day. Expenses logged now will be recorded as overheads.')}
                    </div>
                </div>
            )}

            {/* Daily tab */}
            {tab === 'daily' && (
                <PullToRefresh onRefresh={() => fetchDaily()} disabled={ptrDisabled}>
                    <SurfaceStates
                        status={dailyStatus}
                        onRetry={fetchDaily}
                        emptyTitle={t('no_expenses_today', {}, 'No expenses today')}
                        emptyMsg={t('no_expenses_today_msg', {}, 'Add an expense to get started.')}
                    >
                        <div className="exp-count-line" data-testid="expenses-count-line">
                            {t('n_expenses', { count: dailyExpenses.length }, '{count} Expenses')}
                        </div>
                        <div className="exp-list">
                            {dailyExpenses.map((exp) => (
                                <ExpenseRow
                                    key={exp.expense_id}
                                    expense={exp}
                                    tone="green"
                                    temporal={fmtTime(exp.time)}
                                    currency={currency}
                                    onDelete={openDelete}
                                    t={t}
                                />
                            ))}
                        </div>
                    </SurfaceStates>
                </PullToRefresh>
            )}

            {/* Overhead tab */}
            {tab === 'overhead' && (
                <PullToRefresh onRefresh={() => fetchOverheads()} disabled={ptrDisabled}>
                    <SurfaceStates
                        status={overheadStatus}
                        onRetry={fetchOverheads}
                        emptyTitle={t('no_overheads', {}, 'No overhead expenses')}
                        emptyMsg={t('no_overheads_msg', {}, 'Overhead expenses for the last 30 days will appear here.')}
                    >
                        <div className="exp-count-line" data-testid="expenses-count-line">
                            {t('n_expenses', { count: overheadExpenses.length }, '{count} Expenses')}
                        </div>
                        <div className="exp-list">
                            {overheadExpenses.map((exp) => (
                                <ExpenseRow
                                    key={exp.expense_id}
                                    expense={exp}
                                    tone="blue"
                                    temporal={exp.date}
                                    currency={currency}
                                    onDelete={openDelete}
                                    t={t}
                                />
                            ))}
                        </div>
                    </SurfaceStates>
                </PullToRefresh>
            )}

            {/* FABs — bottom-right, above tab bar */}
            <div className="exp-fabs">
                <button
                    type="button"
                    className="exp-fab exp-fab-scan"
                    data-testid="fab-scan"
                    aria-label={t('scan_receipt', {}, 'Scan Receipt')}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                    <CameraIcon />
                    {t('scan_receipt', {}, 'Scan')}
                </button>
                <button
                    type="button"
                    className="exp-fab exp-fab-add"
                    data-testid="fab-add"
                    aria-label={t(isBusinessDay ? 'add_expense' : 'add_overhead', {}, isBusinessDay ? 'Add Expense' : 'Add Overhead')}
                    onClick={() => { setFormInitialData(null); setFormOpen(true); }}
                >
                    <PlusIcon />
                    {t(isBusinessDay ? 'add_expense' : 'add_overhead', {}, isBusinessDay ? 'Add' : 'Add Overhead')}
                </button>
            </div>

            {/* Hidden file input for receipt scanning */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                data-testid="receipt-file-input"
                onChange={handleFileChange}
            />

            {/* Scan loading overlay */}
            {scanLoading && (
                <div className="exp-scan-overlay" data-testid="scan-loading">
                    <div className="exp-scan-spinner" aria-hidden="true" />
                    <div className="exp-scan-loading-text">
                        {t('scanning_receipt', {}, 'Scanning receipt...')}
                    </div>
                </div>
            )}

            {/* Scan results sheet */}
            {scanResults && !scanLoading && (
                <div className="exp-scan-results" data-testid="scan-results">
                    <GlassCard veil className="exp-scan-sheet">
                        <div className="exp-scan-title">
                            {t('receipt_scanned', {}, 'Receipt Scanned')}
                        </div>
                        {scanResults.map((item, idx) => (
                            <GlassCard
                                key={idx}
                                className="exp-scan-item"
                                data-testid="scan-item"
                                onClick={() => handleScanItemTap(item)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleScanItemTap(item); }}
                            >
                                <div className="exp-scan-item-name">{item.product_name}</div>
                                <div className="exp-scan-item-meta exp-num">
                                    {item.quantity} × {currency}{Number(item.unit_price).toFixed(2)}
                                    {' · '}
                                    {fmtMoney(item.total_cost, currency)}
                                </div>
                            </GlassCard>
                        ))}
                        <button
                            type="button"
                            className="exp-scan-add-all"
                            data-testid="scan-add-all-btn"
                            onClick={handleBatchCreate}
                        >
                            {t('add_all_items', {}, 'Add All Items')}
                        </button>
                        <button
                            type="button"
                            className="exp-scan-close"
                            onClick={() => setScanResults(null)}
                        >
                            {t('cancel', {}, 'Cancel')}
                        </button>
                    </GlassCard>
                </div>
            )}

            {/* Scan error sheet */}
            {scanError && (
                <div className="exp-scan-error" data-testid="scan-error">
                    <GlassCard veil className="exp-scan-error-sheet">
                        <div className="exp-scan-error-text">
                            {t('scan_error', {}, 'Could not scan the receipt. Please try again.')}
                        </div>
                        <button
                            type="button"
                            className="exp-scan-retry"
                            data-testid="scan-retry-btn"
                            onClick={() => {
                                setScanError(false);
                                if (fileInputRef.current) fileInputRef.current.click();
                            }}
                        >
                            {t('try_again', {}, 'Try Again')}
                        </button>
                        <button
                            type="button"
                            className="exp-scan-dismiss"
                            onClick={() => setScanError(false)}
                        >
                            {t('cancel', {}, 'Cancel')}
                        </button>
                    </GlassCard>
                </div>
            )}

            {/* Create expense form (bottom sheet) */}
            <CreateExpenseForm
                open={formOpen}
                onClose={() => { setFormOpen(false); setFormInitialData(null); }}
                onSaved={handleFormSaved}
                isBusinessDay={isBusinessDay}
                salesDate={salesDate}
                eodMode={eodMode}
                categories={currentCats}
                activeSessions={activeSessions}
                username={username}
                inventoryItems={inventoryItems}
                initialData={formInitialData}
                currency={currency}
                t={t}
            />

            {/* Delete confirm sheet */}
            <ConfirmSheet
                open={!!deleteTarget}
                onClose={() => { setDeleteTarget(null); setDeleteReason(''); }}
                onConfirm={handleDelete}
                title={t('delete_expense', {}, 'Delete Expense')}
                message={deleteTarget
                    ? `${deleteTarget.category} — ${deleteTarget.product} (${fmtMoney(deleteTarget.total_cost, currency)})`
                    : ''}
                confirmLabel={t('delete', {}, 'Delete')}
                destructive
                loading={deleteLoading}
                reasonLabel={t('expense_delete_reason', {}, 'Reason for deletion')}
                reasonValue={deleteReason}
                onReasonChange={setDeleteReason}
            />
        </div>
    );
}
